import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  PAYROLL_COMPONENT_CATALOG,
  PAYROLL_GROUP_ORDER,
  type CreatePayrollRunInput,
  type ExtractedPaystub,
  type PayrollComponentGroup,
  type PayrollExtractionResult,
  type PayrollRegisterTotals,
} from '@accounting/shared';
import { ContactPicker } from '../contacts/ContactPicker';
import { FileDropZone } from '../ui/FileDropZone';
import { useExtractPaystub } from '../../hooks/usePayroll';
import { ApiError } from '../../lib/api';
import { formatCurrency, todayIso } from '../../lib/formatters';

export interface StubState {
  employeeName: string;
  employeeContactId: string;
  amounts: Record<string, string>;
  /** This stub's own pay date / period, when the register states one per
   *  employee. Blank inherits the run's — which is how a single-date register
   *  behaves. Salaried and hourly staff run on different cycles, so one
   *  register legitimately carries several dates. */
  payDate?: string;
  periodStart?: string;
  periodEnd?: string;
}

/** Per-file extraction note. One file can contribute 1..N stubs to the form
 *  (one stub for a single paystub PDF, N for a payroll register). The note
 *  captures the file-level context (employer, dates, register totals/bank
 *  drafts, low-confidence warnings) plus pointers to the stubs it produced. */
interface ExtractionNote {
  /** Indices in `stubs` that this extraction filled in (in document order). */
  stubIndices: number[];
  confidence: number;
  source: string;             // file name
  employerName: string | null;
  payDate: string | null;
  /** Register-level warnings (sum-of-stubs vs totals, low confidence, etc.). */
  registerWarnings: string[];
  /** Per-stub warnings keyed by the stub's INDEX in `stubs`. */
  stubWarnings: Record<number, string[]>;
  /** Optional employer-side recap (multi-employee registers only). */
  registerTotals: PayrollRegisterTotals | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Build a fresh stub from one extracted paystub.
 *
 * Dates resolve stub → file → (later) the run. Stamping the FILE's date onto
 * stubs that do not state their own is what makes a second register with a
 * different pay date work: without it those stubs would silently inherit the
 * first file's date.
 */
function stubFromExtracted(p: ExtractedPaystub, file: PayrollExtractionResult): StubState {
  const amounts: Record<string, string> = {};
  for (const c of p.components) amounts[c.componentType] = c.amount;
  return {
    employeeName: p.employeeName ?? '',
    employeeContactId: '',
    amounts,
    payDate: p.payDate ?? file.payDate ?? undefined,
    periodStart: p.periodStart ?? file.periodStart ?? undefined,
    periodEnd: p.periodEnd ?? file.periodEnd ?? undefined,
  };
}

export interface PayrollFormValues {
  payDate: string;
  periodStart: string;
  periodEnd: string;
  memo: string;
  stubs: StubState[];
}

const COMPONENTS_BY_GROUP = PAYROLL_GROUP_ORDER.map((g) => ({
  ...g,
  components: PAYROLL_COMPONENT_CATALOG.filter((c) => c.group === (g.group as PayrollComponentGroup)),
}));

export function emptyStub(): StubState {
  return { employeeName: '', employeeContactId: '', amounts: {} };
}

function num(v: string | undefined): number {
  const n = parseFloat(v ?? '');
  return Number.isFinite(n) ? n : 0;
}

interface Props {
  companyId: string;
  initial?: PayrollFormValues;
  submitLabel: string;
  submitting: boolean;
  errorMessage?: string;
  /** The server rejected this run because a live one already exists for the
   *  pay date. Reveals the override checkbox. */
  duplicatePayDate?: boolean;
  cancelTo: string;
  onSubmit: (input: CreatePayrollRunInput) => void;
}

export function PayrollRunForm({ companyId, initial, submitLabel, submitting, errorMessage, duplicatePayDate, cancelTo, onSubmit }: Props) {
  const [payDate, setPayDate] = useState(initial?.payDate ?? todayIso());
  const [periodStart, setPeriodStart] = useState(initial?.periodStart ?? todayIso());
  const [periodEnd, setPeriodEnd] = useState(initial?.periodEnd ?? todayIso());
  const [memo, setMemo] = useState(initial?.memo ?? '');
  const [stubs, setStubs] = useState<StubState[]>(
    initial?.stubs && initial.stubs.length > 0 ? initial.stubs : [emptyStub()],
  );
  const [notes, setNotes] = useState<ExtractionNote[]>([]);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [allowDuplicatePayDate, setAllowDuplicatePayDate] = useState(false);

  const extract = useExtractPaystub(companyId);

  // Was today's date inferred (still equal to the default) or has the user
  // touched it? We only auto-fill the run dates on the FIRST extraction so we
  // don't blow away later user edits.
  const initialDateUntouched = (d: string) => d === todayIso();

  const onExtractFiles = async (files: File[]) => {
    setExtractError(null);
    // The run's pay date, tracked locally: setPayDate is async, so reading the
    // `payDate` state inside this loop would compare each file against a stale
    // value and miss the mismatch we are looking for.
    let runPayDate = payDate;
    let isFirstExtraction = notes.length === 0;
    for (const file of files) {
      try {
        const content = await fileToBase64(file);
        const result = await extract.mutateAsync({
          fileName: file.name,
          mimeType: 'application/pdf',
          content,
        });

        if (result.paystubs.length === 0) {
          setExtractError(`No paystubs were extracted from "${file.name}".`);
          continue;
        }

        // Auto-fill the run dates from the first successful extraction iff
        // the user hasn't already touched the defaults.
        if (isFirstExtraction) {
          if (result.payDate && initialDateUntouched(payDate)) {
            setPayDate(result.payDate);
            runPayDate = result.payDate;
          }
          if (result.periodStart && initialDateUntouched(periodStart)) setPeriodStart(result.periodStart);
          if (result.periodEnd && initialDateUntouched(periodEnd)) setPeriodEnd(result.periodEnd);
          isFirstExtraction = false;
        }

        // Different pay dates are FINE now — each stub posts its own journal
        // entry on its own date, so salaried and hourly cycles can share one
        // upload. Just say what will happen, rather than warning about it.
        const registerWarnings = [...result.warnings];
        if (result.payDate && runPayDate && result.payDate !== runPayDate) {
          registerWarnings.unshift(
            `This file pays on ${result.payDate}, not ${runPayDate}. Its entries will post on ${result.payDate} — each paystub becomes its own journal entry on its own date.`,
          );
        }

        const newStubs = result.paystubs.map((ps) => stubFromExtracted(ps, result));

        // Where do the new stubs land? Tracked so we can dismiss / re-index
        // notes correctly when the user removes a stub.
        let assignedIndices: number[] = [];
        setStubs((prev) => {
          // If the only existing stub is the default empty one, replace it
          // instead of appending — so a single-paystub upload doesn't leave
          // an orphan empty stub at index 0.
          const onlyDefault =
            prev.length === 1 &&
            !prev[0]!.employeeName.trim() &&
            Object.values(prev[0]!.amounts).every((v) => !v || v === '0');
          if (onlyDefault) {
            assignedIndices = newStubs.map((_, i) => i);
            return newStubs;
          }
          assignedIndices = newStubs.map((_, i) => prev.length + i);
          return [...prev, ...newStubs];
        });

        const stubWarnings: Record<number, string[]> = {};
        result.paystubs.forEach((p, i) => {
          if (p.warnings.length > 0) stubWarnings[assignedIndices[i]!] = p.warnings;
        });

        setNotes((prev) => [
          ...prev,
          {
            stubIndices: assignedIndices,
            confidence: result.overallConfidence,
            source: file.name,
            employerName: result.employerName,
            payDate: result.payDate,
            registerWarnings,
            stubWarnings,
            registerTotals: result.registerTotals,
          },
        ]);
      } catch (err) {
        const msg = err instanceof ApiError
          ? (err.body as { error?: string } | null)?.error ?? 'Extraction failed'
          : err instanceof Error
            ? err.message
            : 'Extraction failed';
        setExtractError(msg);
      }
    }
  };

  // The run-level date boxes are the only place a work period can be typed,
  // but every stub carries its OWN dates and the server derives the header
  // from those — so a header edit that stayed on the header was silently
  // discarded on save. Push it down to the stubs it speaks for: those with no
  // date of their own, those that matched the old header, or all of them when
  // they agree with each other. Stubs on a genuinely different cycle keep theirs.
  const changeRunDate = (
    field: 'payDate' | 'periodStart' | 'periodEnd',
    previous: string,
    next: string,
    setHeader: (v: string) => void,
  ) => {
    setHeader(next);
    if (!next) return;
    setStubs((prev) => {
      const uniform = new Set(prev.map((s) => s[field]).filter(Boolean)).size <= 1;
      return prev.map((s) =>
        uniform || !s[field] || s[field] === previous ? { ...s, [field]: next } : s,
      );
    });
  };

  const dismissNote =(idx: number) => setNotes((prev) => prev.filter((_, i) => i !== idx));

  /** Remove a stub at index `i` and keep notes pointing at the right indices. */
  const removeStub = (i: number) => {
    setStubs((prev) => prev.filter((_, idx) => idx !== i));
    setNotes((prev) =>
      prev
        .map((n) => {
          const indices = n.stubIndices
            .filter((idx) => idx !== i)
            .map((idx) => (idx > i ? idx - 1 : idx));
          const remappedWarnings: Record<number, string[]> = {};
          for (const [k, ws] of Object.entries(n.stubWarnings)) {
            const ki = Number(k);
            if (ki === i) continue;
            remappedWarnings[ki > i ? ki - 1 : ki] = ws;
          }
          return { ...n, stubIndices: indices, stubWarnings: remappedWarnings };
        })
        // A note whose every stub got removed is no longer meaningful.
        .filter((n) => n.stubIndices.length > 0),
    );
  };

  const setStub = (i: number, patch: Partial<StubState>) =>
    setStubs((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const setAmount = (i: number, type: string, value: string) =>
    setStubs((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, amounts: { ...s.amounts, [type]: value } } : s)),
    );

  const { debits, credits } = useMemo(() => {
    let d = 0;
    let c = 0;
    for (const stub of stubs) {
      for (const comp of PAYROLL_COMPONENT_CATALOG) {
        const amt = num(stub.amounts[comp.type]);
        if (amt <= 0) continue;
        if (comp.side === 'debit') d += amt;
        else c += amt;
      }
    }
    return { debits: d, credits: c };
  }, [stubs]);
  const diff = Math.round((debits - credits) * 100) / 100;
  const balanced = Math.abs(diff) < 0.005 && debits > 0;

  const stubGross = (s: StubState) =>
    COMPONENTS_BY_GROUP.find((g) => g.group === 'earnings')!.components.reduce(
      (sum, c) => sum + num(s.amounts[c.type]),
      0,
    );
  const stubNet = (s: StubState) => num(s.amounts['net_pay_bank']);

  const hasStubData = stubs.some(
    (s) => s.employeeName.trim() && PAYROLL_COMPONENT_CATALOG.some((c) => num(s.amounts[c.type]) > 0),
  );
  const valid = payDate && periodStart && periodEnd && periodStart <= periodEnd && balanced && hasStubData;

  const submit = () => {
    const paystubs: CreatePayrollRunInput['paystubs'] = stubs
      .filter((s) => s.employeeName.trim())
      .map((s) => ({
        employeeName: s.employeeName.trim(),
        employeeContactId: s.employeeContactId || undefined,
        ...(s.payDate ? { payDate: s.payDate } : {}),
        ...(s.periodStart ? { periodStart: s.periodStart } : {}),
        ...(s.periodEnd ? { periodEnd: s.periodEnd } : {}),
        components: PAYROLL_COMPONENT_CATALOG.filter((c) => num(s.amounts[c.type]) > 0).map((c) => ({
          componentType: c.type,
          amount: String(num(s.amounts[c.type])),
        })),
      }))
      .filter((s) => s.components.length > 0);

    onSubmit({
      payDate,
      periodStart,
      periodEnd,
      memo: memo.trim() || undefined,
      paystubs,
      ...(allowDuplicatePayDate ? { allowDuplicatePayDate: true } : {}),
    });
  };

  return (
    <div>
      <div className="card" style={{ padding: 16, maxWidth: 760, marginBottom: 16 }}>
        <div className="form-grid">
          <label htmlFor="pr-date">Pay date</label>
          <input id="pr-date" type="date" value={payDate} onChange={(e) => changeRunDate('payDate', payDate, e.target.value, setPayDate)} style={{ maxWidth: 200 }} />
          <label htmlFor="pr-start">Period start</label>
          <input id="pr-start" type="date" value={periodStart} onChange={(e) => changeRunDate('periodStart', periodStart, e.target.value, setPeriodStart)} style={{ maxWidth: 200 }} />
          <label htmlFor="pr-end">Period end</label>
          <input id="pr-end" type="date" value={periodEnd} onChange={(e) => changeRunDate('periodEnd', periodEnd, e.target.value, setPeriodEnd)} style={{ maxWidth: 200 }} />
          <label htmlFor="pr-memo">Memo</label>
          <input id="pr-memo" value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={500} placeholder="optional — defaults to “Payroll — pay date …”" />
        </div>
      </div>

      <div className="card" style={{ padding: 16, maxWidth: 760, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <strong style={{ fontSize: 14 }}>Extract paystubs from PDF</strong>
          <span className="muted" style={{ fontSize: 12 }}>
            Single paystub OR multi-employee payroll register — drop it in.
          </span>
        </div>
        <FileDropZone
          accept="application/pdf"
          multiple
          busy={extract.isPending}
          onFiles={(files) => void onExtractFiles(files)}
          hint="PDF only · 10 MB max · Review every line before posting — AI extraction is a starting point, not the source of truth."
        />
        {extractError && (
          <div className="field-error" style={{ marginTop: 8 }}>{extractError}</div>
        )}
        {notes.map((n, idx) => {
          const hasIssues =
            n.confidence < 0.8 ||
            n.registerWarnings.length > 0 ||
            Object.keys(n.stubWarnings).length > 0;
          const employees = n.stubIndices.map((si) => stubs[si]?.employeeName?.trim() || '(unnamed)');
          return (
            <div
              key={idx}
              style={{
                marginTop: 10,
                padding: 10,
                borderRadius: 6,
                border: `1px solid ${hasIssues ? '#c98f00' : '#1f6b3a'}`,
                background: hasIssues ? 'rgba(201, 143, 0, 0.08)' : 'rgba(31, 107, 58, 0.08)',
                fontSize: 13,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <strong>
                  {n.stubIndices.length === 1
                    ? `Paystub ${n.stubIndices[0]! + 1}`
                    : `${n.stubIndices.length} paystubs (#${n.stubIndices[0]! + 1}–${n.stubIndices[n.stubIndices.length - 1]! + 1})`}{' '}
                  — from {n.source}{' '}
                  <span className="muted" style={{ fontWeight: 400 }}>
                    (confidence {Math.round(n.confidence * 100)}%)
                  </span>
                </strong>
                <button
                  type="button"
                  onClick={() => dismissNote(idx)}
                  className="btn-secondary"
                  style={{ fontSize: 12, padding: '2px 8px' }}
                  aria-label={`Dismiss extraction notice for ${n.source}`}
                >
                  Dismiss
                </button>
              </div>

              {(n.employerName || n.payDate || n.stubIndices.length > 1) && (
                <div className="muted" style={{ marginTop: 4 }}>
                  {n.employerName && <>Employer: <strong>{n.employerName}</strong> · </>}
                  {n.payDate && <>Pay date: <strong>{n.payDate}</strong> · </>}
                  Employees: <strong>{employees.join(', ')}</strong>
                </div>
              )}

              {n.registerTotals && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed currentColor', opacity: 0.85 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Register totals</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 4, fontSize: 12 }}>
                    {n.registerTotals.reportedGross && <span>Gross: <strong>{n.registerTotals.reportedGross}</strong></span>}
                    {n.registerTotals.reportedNet && <span>Net: <strong>{n.registerTotals.reportedNet}</strong></span>}
                    {n.registerTotals.reportedFederalWithheld && <span>Federal w/h: <strong>{n.registerTotals.reportedFederalWithheld}</strong></span>}
                    {n.registerTotals.reportedStateWithheld && <span>State w/h: <strong>{n.registerTotals.reportedStateWithheld}</strong></span>}
                    {n.registerTotals.reportedEmployerTaxes && <span>Employer taxes: <strong>{n.registerTotals.reportedEmployerTaxes}</strong></span>}
                  </div>
                  {n.registerTotals.bankDrafts.length > 0 && (
                    <div style={{ marginTop: 6, fontSize: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Bank drafts (informational — JE-per-draft is a future change):</div>
                      <ul style={{ margin: '0 0 0 18px', padding: 0 }}>
                        {n.registerTotals.bankDrafts.map((d, di) => (
                          <li key={di}>{d.label}: <strong>{d.amount}</strong></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {n.registerWarnings.length > 0 && (
                <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                  {n.registerWarnings.map((w, wi) => (
                    <li key={`r-${wi}`}>{w}</li>
                  ))}
                </ul>
              )}

              {Object.keys(n.stubWarnings).length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {Object.entries(n.stubWarnings).map(([si, ws]) => {
                    const idx = Number(si);
                    const name = stubs[idx]?.employeeName?.trim() || `Paystub ${idx + 1}`;
                    return (
                      <div key={si} style={{ marginTop: 4 }}>
                        <div style={{ fontWeight: 600 }}>{name}:</div>
                        <ul style={{ margin: '2px 0 0 18px', padding: 0 }}>
                          {ws.map((w, wi) => (
                            <li key={wi}>{w}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}

              {!hasIssues && (
                <p className="muted" style={{ margin: '6px 0 0' }}>
                  No warnings — looks good. Review the amounts and post when ready.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {stubs.map((stub, i) => (
        <div key={i} className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>Paystub {i + 1}</strong>
              <input
                value={stub.employeeName}
                onChange={(e) => setStub(i, { employeeName: e.target.value })}
                placeholder="Employee name"
                maxLength={160}
                style={{ minWidth: 220 }}
              />
              <ContactPicker
                companyId={companyId}
                value={stub.employeeContactId}
                onChange={(id) => setStub(i, { employeeContactId: id })}
                contactType="employee"
                defaultNewType="employee"
                placeholder="— link contact (optional) —"
              />
              {/* Each stub posts its own entry on this date. Blank inherits the
                  run's, so a single-date register needs nothing here. */}
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }} className="muted">
                Pays
                <input
                  type="date"
                  value={stub.payDate ?? ''}
                  onChange={(e) => setStub(i, { payDate: e.target.value || undefined })}
                  title="This paystub's own pay date — its journal entry posts on this date. Leave blank to use the run's pay date."
                  style={{ maxWidth: 150 }}
                />
                {(stub.payDate ?? payDate) !== payDate && (
                  <span style={{ color: 'var(--accent)' }}>own cycle</span>
                )}
              </label>
            </div>
            <div style={{ textAlign: 'right', fontSize: 13 }} className="muted">
              Gross {formatCurrency(stubGross(stub))} · Net {formatCurrency(stubNet(stub))}
              {stubs.length > 1 && (
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ marginLeft: 10 }}
                  onClick={() => removeStub(i)}
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginTop: 12 }}>
            {COMPONENTS_BY_GROUP.map((group) => (
              <div key={group.group}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted)', marginBottom: 6 }}>
                  {group.label}
                </div>
                {group.components.map((c) => (
                  <div key={c.type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <label htmlFor={`a-${i}-${c.type}`} style={{ fontSize: 13 }}>
                      {c.label}
                      <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 4 }}>
                        ({c.side === 'debit' ? 'Dr' : 'Cr'})
                      </span>
                    </label>
                    <input
                      id={`a-${i}-${c.type}`}
                      inputMode="decimal"
                      value={stub.amounts[c.type] ?? ''}
                      onChange={(e) => setAmount(i, c.type, e.target.value)}
                      placeholder="0.00"
                      style={{ width: 110, textAlign: 'right' }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}

      <button
        type="button"
        className="btn-secondary"
        onClick={() => setStubs((prev) => [...prev, emptyStub()])}
        style={{ marginBottom: 16 }}
      >
        + Add another paystub
      </button>

      <div className="card" style={{ padding: 16, maxWidth: 760 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
          <span>Total debits</span>
          <span>{formatCurrency(debits)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
          <span>Total credits</span>
          <span>{formatCurrency(credits)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 6, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
          <span>Difference</span>
          <span className={balanced ? 'tag ok' : 'tag'} style={{ fontSize: 13 }}>
            {balanced ? 'Balanced' : formatCurrency(diff)}
          </span>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Employer taxes appear twice — once as an expense (Dr) and once as a payable (Cr) at the same amount.
          The run must balance before it can post.
        </p>

        {errorMessage && <div className="field-error" style={{ marginTop: 8 }}>{errorMessage}</div>}
        {duplicatePayDate && (
          <label
            style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, fontSize: 13 }}
          >
            <input
              type="checkbox"
              checked={allowDuplicatePayDate}
              onChange={(e) => setAllowDuplicatePayDate(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              This really is a <strong>second</strong> payroll on the same pay date (for example a
              bonus run alongside regular pay) — post it anyway. Leave this unticked if you are
              re-uploading a register you already posted.
            </span>
          </label>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn-primary" onClick={submit} disabled={!valid || submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </button>
          <Link to={cancelTo} className="btn-secondary" style={{ textDecoration: 'none' }}>Cancel</Link>
        </div>
      </div>
    </div>
  );
}
